/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Link, useRouter, useTranslations } from "@i18n/next";
import type {
  BibleBookId,
  BibleDeeplinks,
  BibleManifest,
  CloudfrontCookie,
  requestTrustedBibleLinkedContent,
} from "@packages/sdk";
import {
  useInfiniteScroll,
  useRequestBibleCSS,
  useRequestBibleJSON,
  useRequestBibleLinkedContent,
  useRequestInfiniteBibleHTML,
} from "@packages/sdk";
import {
  Button,
  Container,
  END_OF_BROWSER_ASYNC_QUEUE,
  Loader,
  LoadingWrapper,
  Notice,
  spacing,
  useTheme,
} from "@packages/ui";
import {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { I18nMessage } from "../../../../../../@i18n/react";
import { getBibleVerseId } from "../../../lib/utils/getBibleVerseId";
import { BibleEntry } from "../BibleEntryTracker";
import { BibleHeaderContext } from "../BibleHeaderContext";
import { BibleLanguageContext } from "../BibleLanguageProvider";
import { BibleChapter } from "./BibleChapter";

const nextButton: Interpolation = {
  marginBottom: spacing.xxxl,
  marginInline: "auto",
  width: "max-content",
};

// TODO: analytics

type BibleDisplayProps = {
  book: BibleBookId;
  translationId: number;
  translationName: string;
  highlightVerseNo?: number;
  initialChapter: string;
  manifest: BibleManifest;
  cookies: CloudfrontCookie[];
  deeplinks: Awaited<
    ReturnType<typeof requestTrustedBibleLinkedContent>
  >["recommendations"];
};

export const BibleDisplay = ({
  book,
  translationName,
  translationId,
  initialChapter,
  highlightVerseNo,
  manifest,
  cookies,
  deeplinks: unauthdDeeplinks,
}: BibleDisplayProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const { setRtl } = useContext(BibleLanguageContext);
  const { renderedTheme } = useTheme();
  const entryTracker = useContext(BibleEntry);
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);
  const manifestBook = useMemo(
    () => manifest.books.find((bk) => bk.id === book),
    [manifest, book],
  );
  const nextBook = useMemo(() => {
    const bookIdx = manifest.books.findIndex((bk) => bk.id === book);
    if (bookIdx >= 0) return manifest.books[bookIdx + 1];
    return undefined;
  }, [manifest, book]);
  const { data: deeplinks } = useRequestBibleLinkedContent({
    translationId,
    bookId: book,
    initialData: { recommendations: unauthdDeeplinks },
  });
  const t = useTranslations();
  const { chapterRefs } = useContext(BibleHeaderContext);
  const [localLoading, setLocalLoading] = useState<boolean>(true);

  const { data: css } = useRequestBibleCSS({ translationId, cookies });
  const { data: searchable } = useRequestBibleJSON({ translationId, cookies });
  const infiniteQuery = useRequestInfiniteBibleHTML({
    translationId,
    bookAbbr: book,
    initialPageParam: initialChapter,
    manifest,
    cookies,
  });
  useInfiniteScroll({
    query: infiniteQuery,
    screenPercentRemaining: 1,
  });

  useEffect(() => {
    if (
      infiniteQuery.data?.pageParams &&
      !infiniteQuery.data.pageParams.includes(initialChapter.toString())
    ) {
      setLocalLoading(true);
      setHasScrolled(false);
      const firstPageParam = infiniteQuery.data.pageParams[0];
      const lastPageParam =
        infiniteQuery.data.pageParams[infiniteQuery.data.pageParams.length - 1];
      if (isNaN(parseInt(initialChapter.toString()))) {
        // we're looking for an intro
        infiniteQuery.fetchPreviousPage();
      } else {
        // we're looking for a chapter number
        if (
          !isNaN(parseInt(firstPageParam)) &&
          parseInt(firstPageParam) > parseInt(initialChapter.toString())
        ) {
          infiniteQuery.fetchPreviousPage();
        } else if (
          isNaN(parseInt(lastPageParam)) ||
          parseInt(lastPageParam) < parseInt(initialChapter.toString())
        ) {
          infiniteQuery.fetchNextPage();
        }
      }
    } else if (!hasScrolled) {
      /* When we turn off localLoading, the page renders at the top of all
       * available chapters, which triggers the handlePreload useEffect below to
       * load a prior chapter so the user can scroll up -- so if we scroll to
       * our target chapter too quickly, the page jumps when that prior chapter
       * loads in, pushing us ~1 chapter higher than we actually want to go.
       * 100ms manages this better than the usual 10 (END_OF_BROWSER_ASYNC_QUEUE)
       * does. HOWEVER, if localLoading was ALREADY false, this 100ms feels
       * absolutely eternal for users who are expecting to jump straight to a
       * chapter that is already loaded. We don't want localLoading in the
       * dependency array, though, or we'll get ourselves in an infinite loop */
      setLocalLoading(false);
      const scrollTimer = setTimeout(
        () => {
          ref.current
            ?.querySelector(`#_${book}_${initialChapter}`)
            ?.scrollIntoView({ behavior: "instant", block: "start" });
          setHasScrolled(true);
        },
        localLoading ? 100 : END_OF_BROWSER_ASYNC_QUEUE,
      );

      return () => {
        clearTimeout(scrollTimer);
      };
    }
  }, [book, initialChapter, infiniteQuery.data?.pageParams, hasScrolled]);

  const deeplinksByChapter: Record<number, BibleDeeplinks> = useMemo(
    () =>
      (deeplinks?.recommendations ?? []).reduce(
        (prev, cur) => ({ ...prev, [cur.chapter]: cur }),
        {},
      ),
    [deeplinks?.recommendations],
  );

  useEffect(() => {
    if (!manifestBook?.has_intro && isNaN(parseInt(initialChapter))) {
      router.replace(`/bible/${translationName}/${book}/1`, { scroll: false });
    }
  }, [initialChapter, manifestBook]);

  useEffect(() => {
    if (
      !infiniteQuery.isFetchingPreviousPage &&
      infiniteQuery.hasPreviousPage &&
      typeof document !== "undefined"
    ) {
      const scrollBody = document.querySelector("#scrollBody");
      if (!scrollBody) return;
      // this mostly accounts for users scrolling up very very fast, often using PageUp or Home to do so
      if (scrollBody.scrollTop === 0) {
        /* we do this so the scrollTop ISN'T 0, because if it IS 0 when we load earlier content, it will STAY at 0
         * instead of following the current content as it moves down, which will lead to an infinite loop of this
         * function and loading everything all the way back to the beginning of the book */
        scrollBody.scrollBy({ top: 5 });
        infiniteQuery.fetchPreviousPage();
      }
    }
  }, [infiniteQuery.isFetchingPreviousPage, infiniteQuery.hasPreviousPage]);

  useEffect(() => {
    async function handlePreload() {
      await infiniteQuery.fetchPreviousPage();
      ref.current
        ?.querySelector(`#_${book}_${initialChapter}`)
        .scrollIntoView({ behavior: "instant", block: "start" });
    }

    // on initial load, fetch the first previous page so the user can scroll up as desired
    if (
      !infiniteQuery.isPending &&
      infiniteQuery.data?.pages?.length <= 1 &&
      infiniteQuery.hasPreviousPage
    ) {
      handlePreload();
    }
  }, [
    infiniteQuery.hasPreviousPage,
    infiniteQuery.isPending,
    infiniteQuery.data,
    initialChapter,
  ]);

  useEffect(() => {
    entryTracker.setData(translationId, translationName, book, initialChapter);

    return () => {
      entryTracker.setData(null, null, null, null);
    };
  }, [book, initialChapter, translationName, translationId]);

  const scrollToVerse = () => {
    if (!ref.current) {
      setTimeout(scrollToVerse, END_OF_BROWSER_ASYNC_QUEUE);
      return;
    }
    const target = ref.current.querySelector(
      `.verse-${getBibleVerseId({ chapter: parseInt(initialChapter), verse: highlightVerseNo, book, manifest })}`,
    );
    setTimeout(() => {
      if (target) {
        target.scrollIntoView({ block: "center", behavior: "smooth" });
        setHasScrolled(true);
      }
    }, 250);
  };

  useEffect(() => {
    return () => {
      // clean up chapter refs
      for (const key of Object.keys(chapterRefs.current)) {
        if (key.startsWith(book)) {
          delete chapterRefs.current[key];
        }
      }
    };
  }, []);

  useEffect(() => {
    if (highlightVerseNo && !infiniteQuery.isFetching && !hasScrolled) {
      const t = setTimeout(scrollToVerse, END_OF_BROWSER_ASYNC_QUEUE);
      return () => {
        clearTimeout(t);
      };
    }
  }, [highlightVerseNo, infiniteQuery.isFetching, hasScrolled]);

  useEffect(() => {
    setRtl(
      /class="[\w\s]*?rtl[\w\s]*?"/gi.test(infiniteQuery.data?.pages?.[0]),
    );
  }, [infiniteQuery.data]);

  if (infiniteQuery.isError) {
    return (
      <Container size="s" maxWidth>
        <Notice variant="error">
          {t.rich("web_bible_error_message", {
            a: (chunks) => (
              <a href="" onClick={() => location.reload()}>
                {chunks}
              </a>
            ),
          })}
        </Notice>
      </Container>
    );
  }

  // Note: ideally, we wouldn't put a <style> tag inside the <body>, but Next's <Suspense> and <Head>
  // components are diametrically opposed, and <Head> always wins, so if we try to put the <style>
  // tag in the <Head>, it comes out undefined because it never waits for the promise to resolve.
  return (
    <LoadingWrapper isLoading={localLoading}>
      <Suspense>
        <style dangerouslySetInnerHTML={{ __html: css }} />
        <style>{`.web-app-bible button { cursor: pointer; }`}</style>
        <div className={"web-app-bible"} ref={ref}>
          <div
            className={`${renderedTheme === "dark" ? "darkMode" : ""} ${infiniteQuery.data?.pages?.[0]?.includes("with-notes") ? "with-notes" : ""}`}
            css={{ height: spacing.full }}
          >
            {infiniteQuery.isFetchingPreviousPage && <Loader />}
            {infiniteQuery.data?.pages?.map((htmlPage, pageIdx) => {
              const chapterNumStr = infiniteQuery.data.pageParams[pageIdx];
              return (
                <BibleChapter
                  ref={(chapterObj) => {
                    chapterRefs.current[`${book}:${chapterNumStr}`] =
                      chapterObj;
                  }}
                  key={`${book}:${chapterNumStr}`}
                  html={htmlPage}
                  book={book}
                  chapter={chapterNumStr}
                  translationId={translationId}
                  title={`${manifestBook.name_in_chapter_title ?? manifestBook.long_name ?? manifestBook.name}${isNaN(parseInt(chapterNumStr)) ? "" : ` ${chapterNumStr}`}`}
                  deeplinks={deeplinksByChapter[parseInt(chapterNumStr)]}
                  manifest={manifest}
                  searchableJson={searchable}
                />
              );
            })}
            {infiniteQuery.isFetchingNextPage && <Loader />}
          </div>
        </div>
        {!infiniteQuery.isLoading && !infiniteQuery.hasNextPage && nextBook && (
          <Button css={nextButton} size="l" asChild>
            <Link href={`/bible/${translationName}/${nextBook.id}/1`}>
              <I18nMessage
                message="bible_v2_continue_to"
                values={{ 0: nextBook.long_name ?? nextBook.name }}
              />
            </Link>
          </Button>
        )}
      </Suspense>
    </LoadingWrapper>
  );
};
